// Marchés municipaux (H5-01, M08, lot L2-B) : marché, plan vectoriel simple (zones, emplacements et
// éléments en rectangles positionnés, cotes), zones et emplacements typés avec référence, surface,
// état et rubrique de redevance désignée par son code (la rubrique et ses tarifs appartiennent au
// module des recettes, qui seul calcule les montants). Le plan est distinct de la carte territoriale.
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');

export const TYPES_EMPLACEMENT = { boxe: 'Boxe', etal: 'Étal', hangar: 'Hangar', place: 'Place au sol' } as const;
export type TypeEmplacement = keyof typeof TYPES_EMPLACEMENT;
export const CODES_TYPES_EMPLACEMENT = Object.keys(TYPES_EMPLACEMENT) as [TypeEmplacement, ...TypeEmplacement[]];

export const ETATS_EMPLACEMENT = ['disponible', 'attribue', 'reserve', 'hors_service'] as const;
export type EtatEmplacement = (typeof ETATS_EMPLACEMENT)[number];
/** États posés par un agent ; « attribue » ne l'est que par le serveur (attribution en vigueur). */
export const ETATS_EMPLACEMENT_MANUELS = ['disponible', 'reserve', 'hors_service'] as const;

export const PERIODICITES_REDEVANCE = { mensuelle: 1, trimestrielle: 3 } as const;
export type PeriodiciteRedevance = keyof typeof PERIODICITES_REDEVANCE;
export const CODES_PERIODICITES = Object.keys(PERIODICITES_REDEVANCE) as [PeriodiciteRedevance, ...PeriodiciteRedevance[]];

/** Rubrique du catalogue des recettes proposée par défaut (démarche « emplacement de marché »). */
export const RUBRIQUE_EMPLACEMENT_DEFAUT = 'redevance_emplacement';

export const schemaCodeRubrique = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code de rubrique en minuscules, chiffres et soulignés');
export const schemaCodeMarche = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,29}$/, 'Code de 1 à 30 caractères (lettres, chiffres, point, tiret, souligné)');
export const schemaReferencePlan = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,29}$/, 'Référence de 1 à 30 caractères (lettres, chiffres, point, tiret, souligné)');
export const schemaPosition = z.strictObject({ longitude: z.number().min(-180).max(180), latitude: z.number().min(-90).max(90) });
const surface = z.number().positive('Surface positive attendue').max(100_000).multipleOf(0.01, 'Deux décimales au plus');

// Plan -------------------------------------------------------------------------------------

const coordonnee = z.number().min(0).max(10_000);
const rectangle = { x: coordonnee, y: coordonnee, largeur: z.number().positive().max(10_000), hauteur: z.number().positive().max(10_000) };

export const TYPES_ELEMENT_PLAN = ['allee', 'batiment', 'entree', 'sanitaires', 'parking', 'autre'] as const;

export const schemaPlanMarche = z
  .strictObject({
    unite: z.literal('m').default('m'),
    largeur: z.number().positive().max(10_000),
    hauteur: z.number().positive().max(10_000),
    /** Zones du marché (code d'une zone enregistrée). */
    zones: z.array(z.strictObject({ code: schemaReferencePlan, ...rectangle })).max(100).default([]),
    /** Emplacements (référence d'un emplacement enregistré), rotation en degrés. */
    emplacements: z.array(z.strictObject({ reference: schemaReferencePlan, ...rectangle, rotation: z.number().min(-180).max(180).default(0) })).max(2000).default([]),
    /** Éléments de repère sans emplacement (allées, bâtiments, entrées...). */
    elements: z.array(z.strictObject({ type: z.enum(TYPES_ELEMENT_PLAN), libelle: z.string().trim().max(80).optional(), ...rectangle })).max(500).default([]),
    /** Cotes : segment et libellé (« 12 m »). */
    cotes: z
      .array(z.strictObject({ de: z.strictObject({ x: coordonnee, y: coordonnee }), a: z.strictObject({ x: coordonnee, y: coordonnee }), libelle: z.string().trim().min(1).max(40) }))
      .max(500)
      .default([]),
  })
  .superRefine((p, ctx) => {
    const hors = (r: { x: number; y: number; largeur: number; hauteur: number }) => r.x + r.largeur > p.largeur + 1e-9 || r.y + r.hauteur > p.hauteur + 1e-9;
    p.zones.forEach((z, i) => hors(z) && ctx.addIssue({ code: 'custom', path: ['zones', i], message: 'Zone hors du plan' }));
    p.emplacements.forEach((e, i) => hors(e) && ctx.addIssue({ code: 'custom', path: ['emplacements', i], message: 'Emplacement hors du plan' }));
    p.elements.forEach((e, i) => hors(e) && ctx.addIssue({ code: 'custom', path: ['elements', i], message: 'Élément hors du plan' }));
    const doublons = (valeurs: string[]) => valeurs.filter((v, i) => valeurs.indexOf(v) !== i);
    for (const d of new Set(doublons(p.zones.map((z) => z.code)))) ctx.addIssue({ code: 'custom', path: ['zones'], message: `Zone ${d} placée deux fois` });
    for (const d of new Set(doublons(p.emplacements.map((e) => e.reference)))) ctx.addIssue({ code: 'custom', path: ['emplacements'], message: `Emplacement ${d} placé deux fois` });
  });
export type PlanMarche = z.infer<typeof schemaPlanMarche>;

/** Références du plan inconnues du marché (zones et emplacements enregistrés) : liste vide si le plan est cohérent. */
export function referencesInconnuesDuPlan(plan: PlanMarche, connus: { zones: string[]; emplacements: string[] }): { champ: string; message: string }[] {
  const zones = new Set(connus.zones);
  const emplacements = new Set(connus.emplacements);
  return [
    ...plan.zones.flatMap((z, i) => (zones.has(z.code) ? [] : [{ champ: `zones.${i}.code`, message: `Zone inconnue du marché : ${z.code}` }])),
    ...plan.emplacements.flatMap((e, i) => (emplacements.has(e.reference) ? [] : [{ champ: `emplacements.${i}.reference`, message: `Emplacement inconnu du marché : ${e.reference}` }])),
  ];
}

// Marchés, zones, emplacements --------------------------------------------------------------

export const schemaCreationMarche = z.strictObject({
  mode: z.enum(['couvert', 'ciel_ouvert']).default('couvert'),
  /** Absent : le serveur le génère à partir des initiales du nom (D-038), unique dans la collectivité. */
  code: schemaCodeMarche.optional(),
  nom: z.string().trim().min(2).max(200),
  adresse: z.string().trim().max(300).optional(),
  arrondissement_id: uuid.optional(),
  position: schemaPosition.optional(),
  /** Bien du patrimoine (H5-03) : identifiant et référence d'inventaire, sans contrôle tant que l'inventaire n'existe pas. */
  bien_id: uuid.optional(),
  reference_inventaire: z.string().trim().max(60).optional(),
  periodicite_defaut: z.enum(CODES_PERIODICITES).default('mensuelle'),
  plan: schemaPlanMarche.optional(),
});

export const schemaModificationMarche = z
  .strictObject({
    mode: z.enum(['couvert', 'ciel_ouvert']),
    nom: z.string().trim().min(2).max(200),
    adresse: z.string().trim().max(300).nullable(),
    arrondissement_id: uuid.nullable(),
    position: schemaPosition.nullable(),
    bien_id: uuid.nullable(),
    reference_inventaire: z.string().trim().max(60).nullable(),
    periodicite_defaut: z.enum(CODES_PERIODICITES),
    actif: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

export const schemaModificationPlan = z.strictObject({
  plan: schemaPlanMarche,
  /** Version connue du plan : refus si une autre modification l'a précédée (409 plan_modifie). */
  version: z.number().int().nonnegative().optional(),
});

export const schemaCreationZone = z.strictObject({
  code: schemaReferencePlan,
  nom: z.string().trim().min(1).max(120),
  ordre: z.number().int().min(0).max(1000).default(0),
});

export const schemaModificationZone = z
  .strictObject({ nom: z.string().trim().min(1).max(120), ordre: z.number().int().min(0).max(1000) })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

export const schemaCreationEmplacement = z.strictObject({
  zone_id: uuid.optional(),
  reference: schemaReferencePlan,
  type: z.enum(CODES_TYPES_EMPLACEMENT),
  surface_m2: surface,
  rubrique_code: schemaCodeRubrique.default(RUBRIQUE_EMPLACEMENT_DEFAUT),
  etat: z.enum(ETATS_EMPLACEMENT_MANUELS).default('disponible'),
  observations: z.string().trim().max(1000).optional(),
});

export const schemaModificationEmplacement = z
  .strictObject({
    zone_id: uuid,
    type: z.enum(CODES_TYPES_EMPLACEMENT),
    surface_m2: surface,
    rubrique_code: schemaCodeRubrique,
    etat: z.enum(ETATS_EMPLACEMENT_MANUELS),
    /** Motif du changement d'état (obligatoire pour réserver ou mettre hors service). */
    motif: z.string().trim().min(3).max(500),
    observations: z.string().trim().max(1000).nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).some((k) => k !== 'motif'), 'Au moins un champ à modifier')
  .refine((v) => !(v.etat === 'reserve' || v.etat === 'hors_service') || Boolean(v.motif), { message: 'Motif obligatoire pour réserver ou mettre hors service', path: ['motif'] });

export const schemaFiltreMarches = z.strictObject({
  actif: z.enum(['oui', 'non']).optional(),
  arrondissement: uuid.optional(),
});

export const schemaFiltreEmplacements = z.strictObject({
  zone: uuid.optional(),
  etat: z.enum(ETATS_EMPLACEMENT).optional(),
  type: z.enum(CODES_TYPES_EMPLACEMENT).optional(),
});

const jour = z.iso.date('Date AAAA-MM-JJ attendue');

/** Période dont on demande la redevance (affichage de la règle, sans rien émettre). */
export const schemaRequeteTarificationEmplacement = z
  .strictObject({ debut: jour, fin: jour })
  .refine((v) => v.fin >= v.debut, { message: 'La fin suit le début', path: ['fin'] });
